import type { ENUM_VALUES } from '../../platform/analytics/vocabulary'

/**
 * The closed `account_type` set `account_removed` reports — iOS's `AccountType`
 * raw values. Web's wire `type` is Plaid-shaped and coarser (`depository`
 * covers chequing, savings and cash), so the wire `subtype` refines it.
 *
 * Mapping, never defaulting an unknown to a real category:
 *   - `depository` + subtype `checking`/`savings`/`cash` → that value;
 *     `depository` with no or any other subtype (a manual "Chequing or
 *     savings" account has no subtype) → `other`: which of the two it is is
 *     genuinely not known, and guessing `checking` would fabricate a datum.
 *   - `credit`, `investment` → themselves; `brokerage` → `investment`
 *     (`accountGroups` already groups it there).
 *   - `loan` + subtype `mortgage` → `mortgage`, else `loan`.
 *   - Identity: a client-created manual account (iOS `AccountCreateRequest.from`)
 *     stores the app value itself as its wire `type`, with no subtype, so
 *     `checking`, `savings`, `mortgage`, `cash` (and `credit`, `loan`,
 *     `investment`, `other`) map to themselves.
 *   - Any unrecognised type → `other`.
 *
 * Rule order follows tracke5-memory/contracts/analytics/events.json
 * (`account_type_from_wire`); the first match wins and the depository rows
 * precede the identity rows.
 */
export type AnalyticsAccountType = (typeof ENUM_VALUES.account_type)[number]

export function analyticsAccountType(a: {
  type: string
  subtype: string | null
}): AnalyticsAccountType {
  switch (a.type) {
    case 'depository':
      return a.subtype === 'checking' || a.subtype === 'savings' || a.subtype === 'cash'
        ? a.subtype
        : 'other'
    case 'credit':
    case 'investment':
    case 'checking':
    case 'savings':
    case 'mortgage':
    case 'cash':
    case 'other':
      return a.type
    case 'brokerage':
      return 'investment'
    case 'loan':
      return a.subtype === 'mortgage' ? 'mortgage' : 'loan'
    default:
      return 'other'
  }
}

/** Same split as `accountRemovalCopy`: only a manual account is really deleted. */
export function removalMethod(a: { provider: string | null }): 'hide' | 'delete' {
  return a.provider === 'manual' ? 'delete' : 'hide'
}
